import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import {
  Sparkles, Zap, Crown, Rocket, Check, ArrowLeft,
  CreditCard, History, TrendingUp, Gift, X
} from 'lucide-react';
import { toast } from 'sonner';

const PAYMENT_PROVIDERS = [
  { id: 'stripe', name: 'Stripe', icon: '💳', enabled: false },
  { id: 'razorpay', name: 'Razorpay', icon: '🇮🇳', enabled: true },
  { id: 'paypal', name: 'PayPal', icon: '🅿️', enabled: false },
  { id: 'cashfree', name: 'Cashfree', icon: '💸', enabled: false }
];

const CreditPackageCard = ({ pkg, onPurchase }) => {
  const icons = {
    starter: Zap,
    creator: Sparkles,
    pro: Crown,
    studio: Rocket
  };
  const Icon = icons[pkg.id] || Sparkles;

  return (
    <Card className={`bg-gray-900 border-gray-800 relative overflow-hidden transition-all hover:scale-105 hover:border-purple-500/50 ${pkg.popular ? 'ring-2 ring-purple-500' : ''}`}>
      {pkg.popular && (
        <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-600 to-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
          MOST POPULAR
        </div>
      )}
      <CardHeader className="text-center pb-2">
        <div className={`mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${pkg.popular ? 'bg-gradient-to-r from-purple-600 to-cyan-500' : 'bg-gray-800'
          }`}>
          <Icon className={`w-8 h-8 ${pkg.popular ? 'text-white' : 'text-purple-400'}`} />
        </div>
        <CardTitle className="text-xl text-white">{pkg.name}</CardTitle>
        <CardDescription className="text-gray-400">
          {pkg.credits} Credits
          {pkg.bonus > 0 && (
            <span className="ml-2 text-green-400 font-semibold">+{pkg.bonus}% bonus</span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="text-center">
        <div className="mb-6">
          <span className="text-4xl font-bold text-white">₹{pkg.price_inr}</span>
          <span className="text-gray-500 ml-2">INR</span>
          <div className="text-sm text-gray-500 mt-1">
            Approx ${pkg.price} USD
          </div>
        </div>
        <ul className="space-y-2 mb-6 text-left">
          <li className="flex items-center gap-2 text-gray-300">
            <Check className="w-4 h-4 text-green-400" />
            {pkg.credits} AI generations
          </li>
          <li className="flex items-center gap-2 text-gray-300">
            <Check className="w-4 h-4 text-green-400" />
            Never expires
          </li>
          {pkg.bonus > 0 && (
            <li className="flex items-center gap-2 text-gray-300">
              <Check className="w-4 h-4 text-green-400" />
              {pkg.bonus}% extra value
            </li>
          )}
        </ul>
        <Button
          onClick={() => onPurchase(pkg)}
          className={`w-full ${pkg.popular
            ? 'bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600'
            : 'bg-gray-800 hover:bg-gray-700'} text-white`}
        >
          <CreditCard className="w-4 h-4 mr-2" />
          Buy Now
        </Button>
      </CardContent>
    </Card>
  );
};

const CreditsPage = () => {
  const navigate = useNavigate();
  const { token, API } = useContext(AuthContext); // Using API from AuthContext
  const [packages, setPackages] = useState([]);
  const [balance, setBalance] = useState(0);
  const [history, setHistory] = useState({ purchases: [], usage: [] });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('packages');

  // Payment State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState('razorpay');

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    try {
      const [packagesRes, creditsRes, historyRes] = await Promise.all([
        axios.get(`${API}/credits/packages`),
        axios.get(`${API}/ai/credits`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/credits/history`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setPackages(packagesRes.data.packages);
      setBalance(creditsRes.data.credits);
      setHistory(historyRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const handleInitiatePurchase = (pkg) => {
    setSelectedPackage(pkg);
    setShowPaymentModal(true);
  };

  const processPurchase = async () => {
    if (!selectedPackage) return;
    setLoading(true);
    try {
      const isInr = selectedProvider === 'razorpay' || selectedProvider === 'cashfree';
      const response = await axios.post(
        `${API}/credits/purchase`,
        {
          package_id: selectedPackage.id,
          payment_provider: selectedProvider,
          currency: isInr ? 'INR' : 'USD'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = response.data;

      if (selectedProvider === 'stripe') {
        if (data.checkout_url) window.location.href = data.checkout_url;
      }
      else if (selectedProvider === 'paypal') {
        if (data.approval_url) window.location.href = data.approval_url;
      }
      else if (selectedProvider === 'razorpay') {
        const options = {
          key: data.razorpay_key,
          amount: data.amount * 100,
          currency: data.currency,
          name: "ComicMenia",
          description: `Credits: ${selectedPackage.name}`,
          order_id: data.order_id,
          handler: async function (response) {
            try {
              await axios.post(`${API}/credits/verify/razorpay`, {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              }, { headers: { Authorization: `Bearer ${token}` } });
              toast.success("Credits added successfully!");
              setShowPaymentModal(false);
              fetchData();
            } catch (err) {
              toast.error("Payment verification failed");
              console.error(err);
            }
          },
          prefill: data.prefill,
          theme: { color: "#9333ea" }
        };
        const rzp = new window.Razorpay(options);
        rzp.open();
        setLoading(false);
      }
      else if (selectedProvider === 'cashfree') {
        const cashfree = new window.Cashfree({
          mode: data.cashfree_env === 'PROD' ? 'production' : 'sandbox'
        });
        cashfree.checkout({
          paymentSessionId: data.payment_session_id,
          returnUrl: `${window.location.origin}/credits/success?order_id=${data.order_id}&provider=cashfree`
        });
      }

    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate purchase');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-background py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-4">
            <div className="glass-card px-4 py-2 rounded-xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              <span className="text-white font-bold">{balance}</span>
              <span className="text-gray-400">credits</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">AI Credits</h1>
          <p className="text-gray-400 text-lg">Power your comic creations with AI-generated images</p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-4 mb-8">
          <Button
            variant={activeTab === 'packages' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('packages')}
            className={activeTab === 'packages' ? 'bg-purple-600' : 'text-gray-400'}
          >
            <Gift className="w-4 h-4 mr-2" />
            Buy Credits
          </Button>
          <Button
            variant={activeTab === 'history' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('history')}
            className={activeTab === 'history' ? 'bg-purple-600' : 'text-gray-400'}
          >
            <History className="w-4 h-4 mr-2" />
            History
          </Button>
        </div>

        {/* Content */}
        {activeTab === 'packages' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-yellow-500/20">
                    <Sparkles className="w-6 h-6 text-yellow-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Current Balance</p>
                    <p className="text-2xl font-bold text-white">{balance}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-purple-500/20">
                    <TrendingUp className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Total Used</p>
                    <p className="text-2xl font-bold text-white">{history.total_used || 0}</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-gray-900 border-gray-800">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-green-500/20">
                    <CreditCard className="w-6 h-6 text-green-400" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm">Total Purchased</p>
                    <p className="text-2xl font-bold text-white">{history.total_purchased || 0}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Packages Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {packages.map(pkg => (
                <CreditPackageCard
                  key={pkg.id}
                  pkg={pkg}
                  onPurchase={handleInitiatePurchase}
                />
              ))}
            </div>

            {/* Info */}
            <div className="mt-12 text-center text-gray-500 text-sm">
              <p>Secure payment powered by Razorpay. More options coming soon!</p>
              <p className="mt-1">1 credit = 1 AI image generation</p>
            </div>
          </>
        )}

        {activeTab === 'history' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Purchases */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-green-400" />
                  Purchase History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {history.purchases?.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {history.purchases.map((purchase, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-gray-800">
                        <div>
                          <p className="text-white font-medium">{purchase.credits} credits</p>
                          <p className="text-xs text-gray-500">
                            {purchase.created_at ? new Date(purchase.created_at).toLocaleDateString() : 'N/A'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-green-400 font-medium">{purchase.currency} {purchase.amount}</p>
                          <span className={`text-xs px-2 py-0.5 rounded ${purchase.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
                            }`}>
                            {purchase.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No purchases yet</p>
                )}
              </CardContent>
            </Card>

            {/* Usage */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-400" />
                  Usage History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {history.usage?.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {history.usage.map((use, idx) => (
                      <div key={idx} className="p-3 rounded-lg bg-gray-800">
                        <p className="text-white text-sm truncate">{use.prompt}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {use.created_at ? new Date(use.created_at).toLocaleString() : 'N/A'}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No generations yet</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedPackage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md p-6 relative shadow-2xl">
            <button
              onClick={() => setShowPaymentModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="text-2xl font-bold text-white mb-2">Checkout</h2>
            <div className="bg-gray-800/50 p-4 rounded-xl mb-6">
              <p className="text-gray-400 text-sm">Selected Package</p>
              <div className="flex justify-between items-center mt-1">
                <span className="text-white font-medium">{selectedPackage.name}</span>
                <span className="text-purple-400 font-bold">{selectedPackage.credits} Credits</span>
              </div>
              <div className="mt-2 pt-2 border-t border-gray-700 flex justify-between items-center">
                <span className="text-gray-400 text-sm">Price</span>
                <div className="text-right">
                  <div className="text-white font-bold">₹{selectedPackage.price_inr}</div>
                  <div className="text-xs text-gray-500">Approx ${selectedPackage.price} USD</div>
                </div>
              </div>
            </div>

            <p className="text-gray-400 text-sm mb-3">Select Payment Method</p>
            <div className="grid grid-cols-2 gap-3 mb-6">
              {PAYMENT_PROVIDERS.map(provider => (
                <button
                  key={provider.id}
                  onClick={() => provider.enabled && setSelectedProvider(provider.id)}
                  disabled={!provider.enabled}
                  className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-all relative ${!provider.enabled
                    ? 'bg-gray-800/50 border-gray-700/50 text-gray-600 cursor-not-allowed opacity-60'
                    : selectedProvider === provider.id
                      ? 'bg-purple-500/10 border-purple-500 text-white'
                      : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'
                    }`}
                >
                  <span className="text-2xl">{provider.icon}</span>
                  <span className="text-sm font-medium">{provider.name}</span>
                  {!provider.enabled && (
                    <span className="absolute top-1 right-1 text-[10px] bg-gray-700 text-gray-400 px-1.5 py-0.5 rounded-full">
                      Soon
                    </span>
                  )}
                </button>
              ))}
            </div>

            <Button
              onClick={processPurchase}
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-cyan-500 hover:from-purple-700 hover:to-cyan-600 text-white h-12 text-lg font-bold"
            >
              {loading ? 'Processing...' : `Pay via ${PAYMENT_PROVIDERS.find(p => p.id === selectedProvider)?.name}`}
            </Button>

            <p className="text-center text-xs text-gray-500 mt-4">
              Secure payment encryption. By proceeding, you agree to our terms.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditsPage;
